import { Lesson, Lessons } from "@/models/Lesson"
import { Schedules } from "@/models/Schedule"
import { Name } from "@/models/Name"
import { Address } from "@/models/Address"

class Student {
  constructor(
    values
  ) {
    if (!(typeof values.id === 'string')) {
      throw new TypeError(`The type of 'id' must be String. Actual: ${typeof values.id}`)
    }
    if (!(typeof values.parentId === 'string')) {
      throw new TypeError(`The type of 'parentId' must be String. Actual: ${typeof values.parentId}`)
    }
    if (!(values.name instanceof Name)) {
      throw new TypeError(`'name' must be an instance of [object Name]. Actual: ${typeof values.name}`)
    }
    if (!(typeof values.gender === 'string')) {
      throw new TypeError(`The type of 'gender' must be String. Actual: ${typeof values.gender}`)
    }
    if (!(values.birthday instanceof Date)) {
      throw new TypeError(`'birthday' must be an instance of [object Date]. Actual: ${typeof values.birthday}`)
    }
    if (!(values.availableSchedules instanceof Schedules)) {
      throw new TypeError(`'availableSchedules' must be an instance of [object Schedules]. Actual: ${typeof values.availableSchedules}`)
    }
    if (!(values.address instanceof Address)) {
      throw new TypeError(`'address' must be an instance of [object Address]. Actual: ${typeof values.address}`)
    }
    if (!(typeof values.nearestStation === 'string')) {
      throw new TypeError(`The type of 'nearestStation' must be String. Actual: ${typeof values.nearestStation}`)
    }
    if (!(values.teacherIds instanceof Array)) {
      throw new TypeError(`'teacherIds' must be an instance of [object Array]. Actual: ${typeof values.teacherIds}`)
    }
    if (!(values.lessons instanceof Lessons)) {
      throw new TypeError(`'lessons' must be an instance of [object Lessons]. Actual: ${typeof values.lessons}`)
    }
    this.id = values.id
    this.parentId = values.parentId
    this.name = values.name
    this.gender = values.gender
    this.birthday = values.birthday
    this.availableSchedules = values.availableSchedules
    this.address = values.address
    this.nearestStation = values.nearestStation
    this.teacherIds = values.teacherIds
    this.lessons = values.lessons
    this.createdAt = values.createdAt
    this.updatedAt = values.updatedAt
  }

  static validateGrade(birthday) {
    if (!(birthday instanceof Date)) {
      throw new TypeError(`'birthday' must be an instance of [object Date]. Actual: ${typeof birthday}`)
    }
    const base = new Date(birthday.getTime())
    const today = new Date()
    base.setMonth(3)
    base.setDate(2)
    if (base > birthday) {
      base.setFullYear(base.getFullYear()-1)
    }
    base.setMonth(3)
    base.setDate(2)
    const diff = today.getTime() - base.getTime()
    const count = Math.floor(diff/(1000*60*60*24*365))
    return SchoolGrade.fromNumber(count)
  }

  availableSchedulesToStrings() {
    return this.availableSchedules.toStrings()
  }

  birthdayToString() {
    const t = this.birthday.getFullYear()
    const m = this.birthday.getMonth() + 1
    const d = this.birthday.getDate()
    return t + "年" + m + "月" + d + "日"
  }

  checkSchool(schools) {
    if (schools === null) {
      return true
    } else if (schools.some(type => type === this.grade().schoolType)) {
      return true
    } else {
      return false
    }
  }

  checkAvailableDays(availableDays) {
    if (!(availableDays instanceof Array)) {
      throw new TypeError(`'availableDays' must be an instance of Array. Actual: ${typeof availableDays}`)
    }
    return availableDays.some(day => this.availableSchedules.hasDayInAny(day))
  }


  checkPrefecture(prefecture) {
    if (!(typeof prefecture === "string")) {
      throw new TypeError(`The type of 'prefecture' must be String. Actual: ${typeof prefecture}`)
    }
    return (this.address.hasPrefecture(prefecture))
  }

  deleteLesson(id) {
    if (!(typeof id === "string")) {
      throw new TypeError(`The type of 'id' must be an String. Actual: ${typeof id}`)
    }
    this.lessons.get().splice(this.lessons.get().findIndex(lesson => {
      return id === lesson.id
    }), 1)
    return this
  }

  fullName() {
    return this.name.fullName()
  }

  flatFullName() {
    return this.name.flatFullName()
  }

  filterByConditions(conditions) {
    if (!(conditions instanceof Object)) {
      throw new TypeError(`'conditions' must be an instance of [object Object]. Actual: ${typeof conditions}`)
    }
    if (
      this.checkSchool(conditions.schools) &&
      this.checkAvailableDays(conditions.availableDays) &&
      this.checkPrefecture(conditions.prefecture) 
    ) {
      return true
    }
    else if (
      this.checkSchool(conditions.schools) &&
      this.checkAvailableDays(conditions.availableDays) &&
      conditions.prefecture === ""
    ) {
      return true
    }
    else {
      return false
    }
  }

  getId () {
    return this.id
  }

  getParentId () {
    return this.parentId
  }

  getTeacherIds() {
    return this.teacherIds
  }

  getAge () {
    const today = new Date()
    const diff = today.getTime() - this.birthday.getTime()
    const age = Math.floor(diff/(1000*60*60*24*365))
    return age
  }

  grade() {
    const base = new Date(this.birthday.getTime())
    const today = new Date()
    base.setMonth(3)
    base.setDate(2)
    if (base > this.birthday) {
      base.setFullYear(base.getFullYear()-1)
    }
    base.setMonth(3)
    base.setDate(2)
    const diff = today.getTime() - base.getTime()
    const count = Math.floor(diff/(1000*60*60*24*365))
    return SchoolGrade.fromNumber(count)
  }

  getLessons() {
    return this.lessons
  }

  hasLesson(id) {
    if (!(typeof id === "string")) {
      throw new TypeError(`The type of 'id' must be an String. Actual: ${typeof id}`)
    }
    return (this.lessons.find(id))
  }

  hasId(id) {
    if (!(typeof id === "string")) {
      throw new TypeError(`The type of 'id' must be String. Actual: ${typeof id}`)
    }
    return (this.id === id)
  }

  genderInJapanese() {
    return (this.isMale()) ? "男の子" : "女の子"
  }

  isMale () {
    return (this.gender === "male")
  }

  registerTeacherId(teacherId) {
    if (!(typeof teacherId === "string")) {
      throw new TypeError(`The type of 'teacherId' must be String. Actual: ${ typeof teacherId}`)
    }
    if (this.teacherIds.includes(teacherId)) {
      return this
    } else {
      this.teacherIds.push(teacherId)
      return this
    }
  }

  removeTeacherId(teacherId) {
    if (!(typeof teacherId === "string")) {
      throw new TypeError(`The type of 'teacherId' must be string. Actual: ${typeof teacherId}`)
    }
    this.teacherIds.splice(this.teacherIds.findIndex(id => {
      return id === teacherId
    }), 1)
    return this
  }

  registerLesson(lesson) {
    if (!(lesson instanceof Lesson)) {
      throw new TypeError(`'lesson' must be an instance of [object Lesson]. Actual: ${typeof lesson}`)
    }
    this.lessons.register(lesson)
    return this
  }

  toObject() {
    return {
      id: this.id,
      data: {
        parentId: this.parentId,
        personalName: this.name.personalName,
        familyName: this.name.familyName,
        flatPersonalName: this.name.flatPersonalName,
        flatFamilyName: this.name.flatFamilyName,
        gender: this.gender,
        birthday: this.birthday,
        postalCode: this.address.postalCode,
        prefecture: this.address.prefecture,
        municipality: this.address.municipality,
        subAddress: this.address.subAddress,
        availableSchedules: this.availableSchedules.toObjects(),
        nearestStation: this.nearestStation,
        teacherIds: this.teacherIds,
        lessons: this.lessons.toObjects(),
        createdAt: this.createdAt,
        updatedAt: this.updatedAt
      }
    }
  }

}

class Students {
  constructor(students) {
    if (!(students instanceof Array)) {
      throw new TypeError(`'students' must be instance of Array. Actual: ${typeof students}`)
    }
    this.students = students
  }

  toObjects() {
    return this.students.map(student =>  {
      return student.toObject()
    })
  }

  get() {
    return this.students
  }

  length() {
    return this.students.length
  }

  getIds() {
    return this.students.map(student => {
      return student.getId()
    })
  }

  register(student) {
    if (!(student instanceof Student)) {
      throw new TypeError(`'student' must be an instance of [object Student]. Actual: ${student}`)
    }
    this.students.push(student)
    return this
  }

  update(student) {
    if (!(student instanceof Student)) {
      throw new TypeError(`'student' must be an instance of [object Student]. Actual: ${student}`)
    }
    this.students.splice(this.students.findIndex(s => s.id === student.id), 1, student)
    return this
  }

  remove(studentId) {
    if (!(typeof studentId === "string")) {
      throw new TypeError(`The type of 'studentId' must be String. Actual: ${typeof studentId}`)
    }
    this.students.splice(this.students.findIndex(s => s.id === studentId), 1)
    return this
  }

  find(studentId) {
    if (!(typeof studentId === "string")) {
      throw new TypeError(`The type of 'studentId' must be String. Actual: ${typeof studentId}`)
    }
    const result = this.students[this.students.findIndex(s => s.hasId(studentId))]
    return result ? result : null
  }

  filterByTeacherId(teacherId) {
    if (!(typeof teacherId === "string")) {
      throw new TypeError(`The type of 'teacherId' must be String. Actual: ${typeof teacherId}`)
    }
    return new Students(this.students.filter(student => student.teacherIds.includes(teacherId)))
  }

  filterParentId() {
    let parentIds = []
    this.students.forEach(student => {
      if (!parentIds.includes(student.parentId)) {
        parentIds.push(student.parentId)
      }
    })
    return parentIds
  }

  getParentIds() {
    const parentIds = this.students.map(student => {
      return student.parentId
    })
    return parentIds
  }

  findByLessonId(lessonId) {
    if (!(typeof lessonId === 'string')) {
      throw new TypeError(`The type of 'lessonId' must be String. Actual: ${typeof lessonId}`)
    }
    const result = this.students.filter(s => s.hasLesson(lessonId))
    return (result.length === 1) ? result[0] : null
  }
}

class SchoolGrade {
  constructor(schoolType, year) {
    this.schoolType = schoolType
    this.year = year
  }

  static fromNumber(num) {
    if (!(typeof num === "number")) {
      throw new TypeError(`'The type of 'num' must be Number. Actual: ${typeof num}`)
    }
    if (num === 7) return new SchoolGrade(SchoolType.ElementarySchool, Year.First)
    if (num === 8) return new SchoolGrade(SchoolType.ElementarySchool, Year.Second)
    if (num === 9) return new SchoolGrade(SchoolType.ElementarySchool, Year.Third)
    if (num === 10) return new SchoolGrade(SchoolType.ElementarySchool, Year.Fourth)
    if (num === 11) return new SchoolGrade(SchoolType.ElementarySchool, Year.Fifth)
    if (num === 12) return new SchoolGrade(SchoolType.ElementarySchool, Year.Sixth)
    if (num === 13) return new SchoolGrade(SchoolType.JuniorHighSchool, Year.First)
    if (num === 14) return new SchoolGrade(SchoolType.JuniorHighSchool, Year.Second)
    if (num === 15) return new SchoolGrade(SchoolType.JuniorHighSchool, Year.Third)
    if (num === 16) return new SchoolGrade(SchoolType.HighSchool, Year.First)
    if (num === 17) return new SchoolGrade(SchoolType.HighSchool, Year.Second)
    if (num === 18) return new SchoolGrade(SchoolType.HighSchool, Year.Third)
    else {
      throw new RangeError(`The age must be in 7 between 18. Actual: ${num}`)
    }
  }

  toJapanese() {
    let result = ''
    switch(this.schoolType) {
    case SchoolType.ElementarySchool:
      result += '小学'
      break
    case SchoolType.JuniorHighSchool:
      result += '中学'
      break
    case SchoolType.HighSchool:
      result += '高校'
      break
    }
    return `${result}${this.year}年生`
  }
}


const SchoolType = {
  ElementarySchool: 'elementary_school',
  JuniorHighSchool: 'junior_high_school',
  HighSchool: 'high_school'
}

const Year = {
  First: '1',
  Second: '2',
  Third: '3',
  Fourth : '4',
  Fifth: '5',
  Sixth: '6'
}

export { Student, Students, SchoolGrade }